import {jsPDF} from 'jspdf'
import {computeSignLayout, type SignDef, type SignInput} from '../sign-catalog'
import {paintSign} from '../sign-renderer'
import {round4} from '../utils'
import {PdfPainter} from '../painter/pdf-painter'

/**
 * Build an in-memory jsPDF for a sign. Internal — exposed so tests
 * can inspect page metadata. Optional `compress` lets tests turn off
 * FlateDecode so the content stream is plain ASCII for assertions.
 */
export function buildSignPDF(
    def: SignDef,
    input: SignInput,
    opts: {compress?: boolean} = {}
): jsPDF {
    const {layout, arrow} = computeSignLayout(def, input)
    const w = Number(round4(layout.width))
    const h = Number(round4(layout.height))

    const pdf = new jsPDF({
        unit: 'mm',
        format: [w, h],
        orientation: w >= h ? 'landscape' : 'portrait',
        compress: opts.compress ?? true
    })
    pdf.setDocumentProperties({title: `LVS 77-3:2016 sign ${def.code} — ${def.label}`})
    paintSign(new PdfPainter(pdf), layout, input.bgColor, input.strike, arrow)
    return pdf
}

/**
 * Build a vector PDF whose page IS the sign (no margins). One mm = one mm.
 * Every fill/stroke uses LVS-defined DeviceCMYK colors. Returns a Blob
 * ready to download.
 */
export function renderSignToPDF(def: SignDef, input: SignInput): Blob {
    return buildSignPDF(def, input).output('blob')
}
